import React, { useState, useCallback, useEffect } from "react";
import { View, ScrollView, TouchableOpacity, Modal } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Icon } from "@/components/ui/Icon";
import { Feather, FontAwesome6, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { cn } from "@/lib/utils";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import {
  reviewApi,
  type ApplicationDetail,
  type ApprovalChecklistItem,
  type FieldReviewStatus,
  toBackendFieldKey,
  fromBackendFieldKey,
  fieldLabelKey,
} from "@/lib/api/review";
import { PageFooter } from "@/components/ui/PageFooter";
import {
  type ReviewSection,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
  LegalRepSection,
  BusinessInformationSection,
  ProductComplianceSection,
  FinancialSettlementSection,
  BrandingLocalizationSection,
  ApproveConfirmModal,
  RequestChangesModal,
  RejectConfirmModal,
  RejectFieldModal,
  FieldReasonModal,
  EditFieldModal,
  FieldShowEditModal,
} from "./components";
import { toast } from "@/lib/toast";
import { extractApiError } from "@/lib/api/errors";

// ─── Section config (mirrors ApplicationDetailScreen INNER_TABS) ──────────────

const REVIEW_SECTIONS: {
  id: ReviewSection;
  labelKey: TranslationKeys;
  icon: React.ComponentProps<typeof Icon>["name"];
  iconComponent: React.ComponentProps<typeof Icon>["as"];
}[] = [
  {
    id: "legal_representative",
    labelKey: "review.application.subtabs.legalRepresentative",
    icon: "user-circle",
    iconComponent: FontAwesome6,
  },
  {
    id: "business_information",
    labelKey: "review.application.subtabs.businessInformation",
    icon: "briefcase",
    iconComponent: Feather,
  },
  {
    id: "product_compliance",
    labelKey: "review.application.subtabs.productCompliance",
    icon: "shield-check-outline",
    iconComponent: MaterialCommunityIcons,
  },
  {
    id: "financial_settlement",
    labelKey: "review.application.subtabs.financialSettlement",
    icon: "bank-outline",
    iconComponent: MaterialCommunityIcons,
  },
  {
    id: "branding_localization",
    labelKey: "review.application.subtabs.brandingLocalization",
    icon: "color-palette-outline",
    iconComponent: Ionicons,
  },
];

// ─── Main screen ──────────────────────────────────────────────────────────────

export default function ApplicationReviewScreen() {
  const { t, locale, isRTL } = useI18n();
  const router = useRouter();
  const isWideScreen = useIsWideScreen();
  const queryClient = useQueryClient();

  const { id, section: sectionParam } = useLocalSearchParams<{
    id: string;
    section?: ReviewSection;
  }>();

  const activeSection: ReviewSection =
    sectionParam && REVIEW_SECTIONS.some((s) => s.id === sectionParam)
      ? sectionParam
      : "legal_representative";

  const activeSectionIndex = REVIEW_SECTIONS.findIndex((s) => s.id === activeSection);
  const prevSection = REVIEW_SECTIONS[activeSectionIndex - 1] ?? null;
  const nextSection = REVIEW_SECTIONS[activeSectionIndex + 1] ?? null;

  // Per-field audit decisions: fieldKey → approve | reject | edit
  const [fieldDecisions, setFieldDecisions] = useState<Record<string, FieldDecision>>({});
  // Per-field rejection data stored when a reject is confirmed
  const [fieldRejectionData, setFieldRejectionData] = useState<Record<string, FieldRejectionData>>(
    {}
  );
  // Which field's rejection reason modal is open (null = closed)
  const [viewingReasonField, setViewingReasonField] = useState<string | null>(null);
  // Edit field state
  const [fieldEditData, setFieldEditData] = useState<Record<string, FieldEditData>>({});
  const [pendingEditField, setPendingEditField] = useState<{
    fieldKey: string;
    meta: EditFieldMeta;
  } | null>(null);
  const [viewingEditField, setViewingEditField] = useState<string | null>(null);
  const [showDecisions, setShowDecisions] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRequestChangesModal, setShowRequestChangesModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [pendingRejectField, setPendingRejectField] = useState<{
    fieldKey: string;
    meta: RejectFieldMeta;
  } | null>(null);

  const handleRejectField = useCallback((fieldKey: string, meta: RejectFieldMeta) => {
    setPendingRejectField({ fieldKey, meta });
  }, []);

  const invalidateApplication = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["review", "application", id] });
    queryClient.invalidateQueries({ queryKey: ["review", "applications"] });
    queryClient.invalidateQueries({ queryKey: ["review", "field-reviews", id] });
  }, [queryClient, id]);

  // ── Field-level mutations (declared before handlers that reference them) ───
  const fieldReviewMutation = useMutation({
    mutationFn: ({
      uiFieldKey: _uiFieldKey,
      backendFieldKey,
      status,
      reason,
      internalNote,
    }: {
      uiFieldKey: string;
      backendFieldKey: string;
      status: FieldReviewStatus;
      reason?: string;
      internalNote?: string;
    }) => reviewApi.submitFieldReview(id!, backendFieldKey, status, reason, internalNote),
    onSuccess: invalidateApplication,
    onError: (err, { uiFieldKey }) => {
      setFieldDecisions((prev) => {
        const next = { ...prev };
        delete next[uiFieldKey];
        return next;
      });
      toast.error({ message: extractApiError(err, t("common.error.generic")) });
    },
  });

  const deleteFieldReviewMutation = useMutation({
    mutationFn: ({
      backendFieldKey,
    }: {
      uiFieldKey: string;
      backendFieldKey: string;
      prevDecision: FieldDecision;
      prevRejectionData?: FieldRejectionData;
    }) => reviewApi.deleteFieldReview(id!, backendFieldKey),
    onSuccess: invalidateApplication,
    onError: (err, { uiFieldKey, prevDecision, prevRejectionData }) => {
      setFieldDecisions((prev) => ({ ...prev, [uiFieldKey]: prevDecision }));
      if (prevRejectionData) {
        setFieldRejectionData((prev) => ({ ...prev, [uiFieldKey]: prevRejectionData }));
      }
      toast.error({ message: extractApiError(err, t("common.error.generic")) });
    },
  });

  const fieldEditMutation = useMutation({
    mutationFn: ({
      uiFieldKey: _uiFieldKey,
      backendFieldKey,
      value,
    }: {
      uiFieldKey: string;
      backendFieldKey: string;
      value: string;
    }) => reviewApi.submitFieldEdit(id!, backendFieldKey, value),
    onSuccess: invalidateApplication,
    onError: (err, { uiFieldKey }) => {
      setFieldDecisions((prev) => {
        const next = { ...prev };
        delete next[uiFieldKey];
        return next;
      });
      setFieldEditData((prev) => {
        const next = { ...prev };
        delete next[uiFieldKey];
        return next;
      });
      toast.error({ message: extractApiError(err, t("common.error.generic")) });
    },
  });

  const deleteFieldEditMutation = useMutation({
    mutationFn: ({
      backendFieldKey,
    }: {
      uiFieldKey: string;
      backendFieldKey: string;
      prevEditData: FieldEditData;
    }) => reviewApi.deleteFieldEdit(id!, backendFieldKey),
    onSuccess: invalidateApplication,
    onError: (err, { uiFieldKey, prevEditData }) => {
      setFieldDecisions((prev) => ({ ...prev, [uiFieldKey]: "edit" }));
      setFieldEditData((prev) => ({ ...prev, [uiFieldKey]: prevEditData }));
      toast.error({ message: extractApiError(err, t("common.error.generic")) });
    },
  });

  const handleFieldDecide = useCallback(
    (fieldKey: string, decision: FieldDecision) => {
      setFieldDecisions((prev) => ({ ...prev, [fieldKey]: decision }));
      // "reject" and "edit" decisions are submitted via their modal onConfirm handlers
      if (decision === "approve") {
        fieldReviewMutation.mutate({
          uiFieldKey: fieldKey,
          backendFieldKey: toBackendFieldKey(fieldKey),
          status: "approved",
        });
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleUndoDecision = useCallback(
    (fieldKey: string) => {
      const currentDecision = fieldDecisions[fieldKey];
      const prevRejectionData = fieldRejectionData[fieldKey];
      const prevEditData = fieldEditData[fieldKey];
      setFieldDecisions((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      setFieldRejectionData((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      setFieldEditData((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      const backendFieldKey = toBackendFieldKey(fieldKey);
      if (currentDecision === "edit") {
        deleteFieldEditMutation.mutate({
          uiFieldKey: fieldKey,
          backendFieldKey,
          prevEditData: prevEditData!,
        });
      } else if (currentDecision === "approve" || currentDecision === "reject") {
        deleteFieldReviewMutation.mutate({
          uiFieldKey: fieldKey,
          backendFieldKey,
          prevDecision: currentDecision,
          prevRejectionData,
        });
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldDecisions, fieldRejectionData, fieldEditData]
  );

  const handleShowReason = useCallback((fieldKey: string) => {
    setViewingReasonField(fieldKey);
  }, []);

  const handleEditField = useCallback((fieldKey: string, meta: EditFieldMeta) => {
    setPendingEditField({ fieldKey, meta });
  }, []);

  const handleShowEdit = useCallback((fieldKey: string) => {
    setViewingEditField(fieldKey);
  }, []);

  const fieldsAuditedCount = Object.keys(fieldDecisions).length;

  const { data: application, isLoading } = useQuery<ApplicationDetail>({
    queryKey: ["review", "application", id],
    queryFn: () => reviewApi.getApplication(id!),
    enabled: !!id,
  });

  // ── Field reviews: load existing state from API ───────────────────────────
  const { data: fieldReviewsData } = useQuery({
    queryKey: ["review", "field-reviews", id],
    queryFn: () => reviewApi.getFieldReviews(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!fieldReviewsData) return;

    const decisions: Record<string, FieldDecision> = {};
    const rejections: Record<string, FieldRejectionData> = {};
    const edits: Record<string, FieldEditData> = {};

    for (const review of fieldReviewsData.reviews) {
      const uiKey = fromBackendFieldKey(review.field_key);

      decisions[uiKey] = review.status === "approved" ? "approve" : "reject";
      if (review.status === "rejected") {
        rejections[uiKey] = {
          reason: review.reason ?? "",
          note: review.internal_note ?? undefined,
          rejectedAt: review.created_at,
          rejectedBy: { name: review.reviewer_name, role: "Admin" },
        };
      }
    }

    for (const edit of fieldReviewsData.edits) {
      const uiKey = fromBackendFieldKey(edit.field_key);
      // The API returns all historical edits, most-recent first.
      // Skip if we already stored the latest edit for this key.
      if (edits[uiKey]) continue;
      decisions[uiKey] = "edit";
      edits[uiKey] = {
        newValue: edit.new_value,
        previousValue: edit.old_value ?? undefined,
        fieldLabel: t(fieldLabelKey(uiKey) as TranslationKeys),
      };
    }

    setFieldDecisions(decisions);
    setFieldRejectionData(rejections);
    setFieldEditData(edits);
  }, [fieldReviewsData, t]);

  const decisionMutation = useMutation({
    mutationFn: (body: Parameters<typeof reviewApi.submitDecision>[1]) =>
      reviewApi.submitDecision(id!, body),
    onSuccess: invalidateApplication,
  });

  const handleApproveSubmit = useCallback(
    async (checklist: ApprovalChecklistItem[]) => {
      await decisionMutation.mutateAsync({ action: "approve", checklist });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleRejectSubmit = useCallback(
    async (reason: string) => {
      await decisionMutation.mutateAsync({ action: "reject", reason });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleRequestChangesSubmit = useCallback(
    async (requestedChanges: { field_key: string; reason: string }[]) => {
      await decisionMutation.mutateAsync({
        action: "request_changes",
        requested_changes: requestedChanges,
      });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSectionChange = useCallback(
    (section: ReviewSection) => {
      router.setParams({ section });
    },
    [router]
  );

  const handleCancel = useCallback(() => {
    router.back();
  }, [router]);

  const handlePrevSection = useCallback(() => {
    if (prevSection) {
      router.setParams({ section: prevSection.id });
    }
  }, [router, prevSection]);

  const handleNextSection = useCallback(() => {
    if (nextSection) {
      router.setParams({ section: nextSection.id });
    }
  }, [router, nextSection]);

  if (isLoading) {
    return (
      <View className="flex-1 bg-stroke-10 items-center justify-center">
        <AppText className="text-sm text-dark-text-15">{t("common.loading")}</AppText>
      </View>
    );
  }

  return (
    <>
      <View className="flex-1 bg-stroke-10">
        {/* Breadcrumb */}
        <View className="px-page-x bg-white py-4 border-b border-stroke-50">
          <Breadcrumb
            segments={[
              { labelKey: "review.dashboard.title", href: "/(staff)/(dashboard)/applications" },
              {
                labelText:
                  (locale === "en"
                    ? application?.businessInformation?.companyNameEnglish
                    : locale === "ar"
                      ? application?.businessInformation?.companyNameArabic
                      : application?.businessInformation?.companyNameChinese) ??
                  application?.companyName ??
                  "—",
                href: `/(staff)/applications/${id}`,
              },
              { labelKey: "review.review.title" },
            ]}
          />
        </View>
        {/* ── Scrollable body ────────────────────────────────────────── */}
        <ScrollView className="flex-1 bg-white" contentContainerClassName="flex-grow-1">
          {/* Header */}
          <View className="bg-white border-b border-stroke-50 py-6">
            <View className="px-page-x w-full">
              <View className="w-full max-w-8xl self-center flex-col tablet:flex-row items-start justify-between gap-4">
                <View className="gap-2">
                  <AppText
                    weight="semibold"
                    className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
                  >
                    {t("review.review.title")}
                  </AppText>
                  <View className="flex-row items-center gap-3">
                    <AppText className="text-base text-supporting-text-40">
                      {t("review.review.sectionProgress", {
                        current: String(activeSectionIndex + 1),
                        total: String(REVIEW_SECTIONS.length),
                      })}
                    </AppText>
                    <View className="w-2 h-2 rounded-full bg-stroke-60" />
                    <AppText className="text-base text-supporting-text-40">
                      {t("review.review.fieldsAudited", { count: String(fieldsAuditedCount) })}
                    </AppText>
                  </View>
                </View>

                {/* Action buttons */}
                <View className="flex-row flex-wrap items-center gap-4">
                  {prevSection ? (
                    <Button
                      label={prevSection.labelKey}
                      variant="tertiary"
                      onPress={handlePrevSection}
                      icon={
                        <Icon
                          as={Feather}
                          name={isRTL ? "arrow-right" : "arrow-left"}
                          size={16}
                          className="text-primary"
                        />
                      }
                    />
                  ) : (
                    <Button
                      label="common.cancel"
                      variant="ghost"
                      onPress={handleCancel}
                      labelClassName="text-error"
                    />
                  )}
                  {nextSection ? (
                    <Button
                      label={nextSection.labelKey}
                      variant="primary"
                      onPress={handleNextSection}
                      trailingIcon={
                        <Icon
                          as={Feather}
                          name={isRTL ? "arrow-left" : "arrow-right"}
                          size={16}
                          className="text-white"
                        />
                      }
                    />
                  ) : (
                    <Button
                      label="review.review.reviewCompleted"
                      variant="primary"
                      onPress={() => setShowDecisions(true)}
                    />
                  )}
                </View>
              </View>
            </View>
          </View>

          {/* Body: sidebar + content */}
          <View className="px-page-x py-10">
            <View className="flex-col tablet:flex-row gap-6 w-full max-w-8xl self-center">
              {/* ── Left sidebar ─────────────────────────────────────── */}
              {isWideScreen && (
                <View className="w-60 lg:w-72 flex-shrink-0 gap-2">
                  {REVIEW_SECTIONS.map((section, index) => {
                    const isActive = activeSection === section.id;
                    const isCompleted = index < activeSectionIndex;
                    return (
                      <TouchableOpacity
                        key={section.id}
                        onPress={() => handleSectionChange(section.id)}
                        className={cn(
                          "flex-row items-center gap-3 px-4 py-3 rounded",
                          isActive
                            ? "bg-primary-5"
                            : isCompleted
                              ? "bg-green-5 border border-green-50"
                              : "bg-white border border-stroke-50"
                        )}
                      >
                        <Icon
                          as={section.iconComponent}
                          name={section.icon}
                          size={18}
                          className={
                            isActive
                              ? "text-primary"
                              : isCompleted
                                ? "text-green-50"
                                : "text-dark-text-30"
                          }
                        />
                        <AppText
                          weight="medium"
                          className={cn(
                            "text-base flex-1",
                            isActive
                              ? "text-dark-text-50"
                              : isCompleted
                                ? "text-green-50"
                                : "text-dark-text-20"
                          )}
                        >
                          {t(section.labelKey as TranslationKeys)}
                        </AppText>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {/* Mobile section strip */}
              {!isWideScreen && (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  className="flex-grow-0"
                  contentContainerStyle={{ gap: 8, paddingBottom: 8 }}
                >
                  {REVIEW_SECTIONS.map((section, index) => {
                    const isActive = activeSection === section.id;
                    const isCompleted = index < activeSectionIndex;
                    return (
                      <TouchableOpacity
                        key={section.id}
                        onPress={() => handleSectionChange(section.id)}
                        className={cn(
                          "flex-row items-center gap-2 px-3 py-2 rounded",
                          isActive
                            ? "bg-primary-5"
                            : isCompleted
                              ? "bg-green-5 border border-green-50"
                              : "bg-white border border-stroke-50"
                        )}
                      >
                        <Icon
                          as={section.iconComponent}
                          name={section.icon}
                          size={16}
                          className={
                            isActive
                              ? "text-primary"
                              : isCompleted
                                ? "text-green-50"
                                : "text-dark-text-30"
                          }
                        />
                        <AppText
                          weight="medium"
                          className={cn(
                            "text-sm",
                            isActive
                              ? "text-primary"
                              : isCompleted
                                ? "text-green-50"
                                : "text-dark-text-50"
                          )}
                        >
                          {t(section.labelKey as TranslationKeys)}
                        </AppText>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              )}

              {/* ── Content card ──────────────────────────────────────── */}
              <View className="flex-1 bg-white border border-stroke-50 rounded-lg overflow-hidden">
                {activeSection === "legal_representative" && (
                  <LegalRepSection
                    legalRep={application?.legalRepresentative}
                    fieldDecisions={fieldDecisions}
                    fieldRejectionData={fieldRejectionData}
                    onDecide={handleFieldDecide}
                    onRejectField={handleRejectField}
                    onUndoDecision={handleUndoDecision}
                    onShowReason={handleShowReason}
                    fieldEditData={fieldEditData}
                    onEditField={handleEditField}
                    onShowEdit={handleShowEdit}
                    t={t}
                    applicationId={id}
                  />
                )}
                {activeSection === "business_information" && (
                  <BusinessInformationSection
                    bizInfo={application?.businessInformation}
                    fieldDecisions={fieldDecisions}
                    fieldRejectionData={fieldRejectionData}
                    onDecide={handleFieldDecide}
                    onRejectField={handleRejectField}
                    onUndoDecision={handleUndoDecision}
                    onShowReason={handleShowReason}
                    fieldEditData={fieldEditData}
                    onEditField={handleEditField}
                    onShowEdit={handleShowEdit}
                    t={t}
                    applicationId={id}
                  />
                )}
                {activeSection === "product_compliance" && (
                  <ProductComplianceSection
                    compliance={application?.productCompliance}
                    fieldDecisions={fieldDecisions}
                    fieldRejectionData={fieldRejectionData}
                    onDecide={handleFieldDecide}
                    onRejectField={handleRejectField}
                    onUndoDecision={handleUndoDecision}
                    onShowReason={handleShowReason}
                    fieldEditData={fieldEditData}
                    onEditField={handleEditField}
                    onShowEdit={handleShowEdit}
                    t={t}
                    applicationId={id}
                  />
                )}
                {activeSection === "financial_settlement" && (
                  <FinancialSettlementSection
                    settlement={application?.financialSettlement}
                    fieldDecisions={fieldDecisions}
                    fieldRejectionData={fieldRejectionData}
                    onDecide={handleFieldDecide}
                    onRejectField={handleRejectField}
                    onUndoDecision={handleUndoDecision}
                    onShowReason={handleShowReason}
                    fieldEditData={fieldEditData}
                    onEditField={handleEditField}
                    onShowEdit={handleShowEdit}
                    t={t}
                    applicationId={id}
                  />
                )}
                {activeSection === "branding_localization" && (
                  <BrandingLocalizationSection
                    branding={application?.brandingLocalization}
                    fieldDecisions={fieldDecisions}
                    fieldRejectionData={fieldRejectionData}
                    onDecide={handleFieldDecide}
                    onRejectField={handleRejectField}
                    onUndoDecision={handleUndoDecision}
                    onShowReason={handleShowReason}
                    fieldEditData={fieldEditData}
                    onEditField={handleEditField}
                    onShowEdit={handleShowEdit}
                    t={t}
                    applicationId={id}
                  />
                )}
              </View>
            </View>
          </View>
          <View className="h-52 mt-20">
            <View>
              <PageFooter />
            </View>
          </View>
        </ScrollView>

        {/* ── Overlay + Sticky footer via Modal (covers header) ───────── */}
        <Modal
          visible={showDecisions}
          transparent
          animationType="fade"
          statusBarTranslucent
          onRequestClose={() => setShowDecisions(false)}
        >
          <View className="flex-1">
            {/* Overlay — tap to dismiss */}
            <TouchableOpacity
              activeOpacity={1}
              onPress={() => setShowDecisions(false)}
              className="flex-1 bg-dark-text-50/40"
            />
            {/* Decision panel at the bottom */}
            <View className="bg-white border-t border-stroke-50 px-page-x w-full py-6">
              <View className="w-full max-w-8xl self-center flex-row gap-6">
                <View className="w-60 lg:w-72 flex-shrink-0 gap-2 hidden tablet:flex" />
                <View className="flex-1 bg-white border border-stroke-50 rounded-lg overflow-hidden">
                  <View className="flex-row items-center justify-between px-6 py-5 gap-4">
                    <View className="gap-2">
                      <AppText weight="semibold" className="text-lg text-dark-text-50">
                        {t("review.review.decision.title")}
                      </AppText>
                      <AppText className="text-base text-supporting-text-40">
                        {t("review.review.decision.description")}
                      </AppText>
                    </View>
                    <TouchableOpacity onPress={() => setShowDecisions(false)}>
                      <Icon as={Feather} name="x" size={20} className="text-dark-text-50" />
                    </TouchableOpacity>
                  </View>
                  <View className="h-px bg-stroke-50 w-full mb-5" />
                  <View className="flex-row flex-wrap gap-3 px-6 pb-5">
                    <Button
                      label="review.review.decision.approve"
                      variant="success"
                      icon={
                        <Icon as={Feather} name="check-circle" size={16} className="text-white" />
                      }
                      onPress={() => {
                        setShowDecisions(false);
                        setShowApproveModal(true);
                      }}
                    />
                    <Button
                      label="review.review.decision.requestChanges"
                      variant="outline-warning"
                      icon={
                        <Icon as={Feather} name="refresh-cw" size={16} className="text-orange" />
                      }
                      onPress={() => {
                        setShowDecisions(false);
                        setShowRequestChangesModal(true);
                      }}
                    />
                    <Button
                      label="review.review.decision.reject"
                      variant="secondary-destructive"
                      icon={<Icon as={Feather} name="x-circle" size={16} className="text-error" />}
                      onPress={() => {
                        setShowDecisions(false);
                        setShowRejectModal(true);
                      }}
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* ── Sticky footer: Application Decision (collapsed state) ────── */}
        <View className="absolute bottom-0 start-0 end-0 bg-white border-t border-stroke-50 px-page-x w-full py-6">
          <View className="w-full max-w-8xl self-center flex-row gap-6">
            <View className="w-60 lg:w-72 flex-shrink-0 gap-2 hidden tablet:flex" />
            <View className="flex-1 bg-white border border-stroke-50 rounded-lg overflow-hidden">
              <View className="flex-row flex-wrap items-center justify-between px-6 py-5 gap-4">
                <View className="gap-2">
                  <AppText weight="semibold" className="text-lg text-dark-text-50">
                    {t("review.review.decision.title")}
                  </AppText>
                  <AppText className="text-base text-supporting-text-40">
                    {t("review.review.decision.description")}
                  </AppText>
                </View>
                <Button
                  label="review.review.decision.showDecisions"
                  variant="solid"
                  onPress={() => setShowDecisions(true)}
                />
              </View>
            </View>
          </View>
        </View>
      </View>

      <ApproveConfirmModal
        visible={showApproveModal}
        onDismiss={() => setShowApproveModal(false)}
        onConfirm={() => {
          setShowApproveModal(false);
          router.back();
        }}
        isSubmitting={decisionMutation.isPending}
        onSubmit={handleApproveSubmit}
        t={t}
      />
      <RequestChangesModal
        visible={showRequestChangesModal}
        onDismiss={() => setShowRequestChangesModal(false)}
        onConfirm={() => {
          setShowRequestChangesModal(false);
          router.back();
        }}
        application={application}
        isSubmitting={decisionMutation.isPending}
        onSubmit={handleRequestChangesSubmit}
        t={t}
        rejectedFieldKeys={Object.entries(fieldDecisions)
          .filter(([, d]) => d === "reject")
          .map(([key]) => key)}
        rejectedReasons={Object.fromEntries(
          Object.entries(fieldRejectionData).map(([key, data]) => [key, data.reason])
        )}
      />
      <RejectConfirmModal
        visible={showRejectModal}
        onDismiss={() => setShowRejectModal(false)}
        onConfirm={() => {
          setShowRejectModal(false);
          router.back();
        }}
        vendorName={application?.companyName}
        isSubmitting={decisionMutation.isPending}
        onSubmit={handleRejectSubmit}
        t={t}
      />
      <RejectFieldModal
        visible={!!pendingRejectField}
        onDismiss={() => setPendingRejectField(null)}
        applicationId={id}
        onConfirm={(reason, note) => {
          if (pendingRejectField) {
            handleFieldDecide(pendingRejectField.fieldKey, "reject");
            setFieldRejectionData((prev) => ({
              ...prev,
              [pendingRejectField.fieldKey]: {
                reason,
                note: note || undefined,
                rejectedAt: new Date().toISOString(),
                rejectedBy: { name: "Nina Ross", role: "Admin" },
                fieldLabel: pendingRejectField.meta.label,
                fieldValue: pendingRejectField.meta.value,
                fieldFileKey: pendingRejectField.meta.fileKey,
              },
            }));
            fieldReviewMutation.mutate({
              uiFieldKey: pendingRejectField.fieldKey,
              backendFieldKey: toBackendFieldKey(pendingRejectField.fieldKey),
              status: "rejected",
              reason,
              internalNote: note || undefined,
            });
            setPendingRejectField(null);
          }
        }}
        fieldMeta={pendingRejectField?.meta}
        initialReason={
          pendingRejectField ? (fieldRejectionData[pendingRejectField.fieldKey]?.reason ?? "") : ""
        }
        initialNote={
          pendingRejectField ? (fieldRejectionData[pendingRejectField.fieldKey]?.note ?? "") : ""
        }
        t={t}
      />
      <FieldReasonModal
        visible={!!viewingReasonField}
        onDismiss={() => setViewingReasonField(null)}
        applicationId={id}
        onEdit={() => {
          const fieldKey = viewingReasonField!;
          const rejData = fieldRejectionData[fieldKey];
          setViewingReasonField(null);
          setPendingRejectField({
            fieldKey,
            meta: {
              label: rejData?.fieldLabel ?? fieldKey,
              value: rejData?.fieldValue,
              fileKey: rejData?.fieldFileKey,
            },
          });
        }}
        onUndoDecision={() => {
          if (viewingReasonField) {
            handleUndoDecision(viewingReasonField);
            setViewingReasonField(null);
          }
        }}
        data={viewingReasonField ? fieldRejectionData[viewingReasonField] : undefined}
        t={t}
      />
      <EditFieldModal
        visible={!!pendingEditField}
        onDismiss={() => setPendingEditField(null)}
        onConfirm={(newValue) => {
          if (pendingEditField) {
            handleFieldDecide(pendingEditField.fieldKey, "edit");
            setFieldEditData((prev) => ({
              ...prev,
              [pendingEditField.fieldKey]: {
                newValue,
                // Preserve the original app value on subsequent re-edits; fall back to
                // meta.currentValue only on the very first edit (when no entry exists yet).
                previousValue:
                  prev[pendingEditField.fieldKey]?.previousValue ??
                  pendingEditField.meta.currentValue,
                fieldLabel: pendingEditField.meta.label,
                multiline: pendingEditField.meta.multiline,
              },
            }));
            fieldEditMutation.mutate({
              uiFieldKey: pendingEditField.fieldKey,
              backendFieldKey: toBackendFieldKey(pendingEditField.fieldKey),
              value: newValue,
            });
            setPendingEditField(null);
          }
        }}
        fieldMeta={pendingEditField?.meta}
        initialValue={
          pendingEditField ? (fieldEditData[pendingEditField.fieldKey]?.newValue ?? "") : ""
        }
        t={t}
      />
      <FieldShowEditModal
        visible={!!viewingEditField}
        onDismiss={() => setViewingEditField(null)}
        onUpdate={() => {
          const fieldKey = viewingEditField!;
          const editData = fieldEditData[fieldKey];
          setViewingEditField(null);
          if (editData) {
            setPendingEditField({
              fieldKey,
              meta: {
                label: editData.fieldLabel,
                currentValue: editData.newValue,
                multiline: editData.multiline,
              },
            });
          }
        }}
        onUndoEdit={() => {
          if (viewingEditField) {
            handleUndoDecision(viewingEditField);
            setViewingEditField(null);
          }
        }}
        fieldLabel={viewingEditField ? fieldEditData[viewingEditField]?.fieldLabel : undefined}
        data={viewingEditField ? fieldEditData[viewingEditField] : undefined}
        t={t}
      />
    </>
  );
}
